import { useCallback, useMemo } from 'react';
import {
  Offer,
  OfferType,
  Product,
  ProductPriceUnit,
  ProductType,
  Recurrence,
  ShortOfferDto,
  useSendAction,
} from 'queries';
import { useTrackEvent } from '@app/analytics';
import { OfferItem } from './OfferItem';
import { OfferCriteriaSubList } from './OfferCriteriaSubList';
import { getRelevantProducts } from 'lib/planAndOffer';
import { OfferBlockDisplay } from './OfferBlockDisplay';
import {
  DisplayablePrice,
  DisplayablePriceUnit,
} from './utils/useDisplayablePriceList';
import { useTranslation } from 'react-i18next';
import { OfferCreditItems } from './OfferCreditItems';
import { isProductDisplayable } from './utils/isProductDisplayable';
import { ProductOfferItem } from './ProductOfferItem';
import { compareProducts, OfferProductOrdersInfos } from '../../lib/products';
import { OfferScreenerIncluded } from './OfferScreenerIncluded';
import { PriceBlock } from './PriceBlock';
import { PricesByCountries } from './PricesByCountries';
import { PriceChips } from '../PriceChips';
import {
  computeIncentive,
  NO_PRICE_GROUP,
  sumProducts,
} from 'lib/priceComputation';

const allCriteria = [
  'birthdate',
  'gender',
  'countryKey',
  'city',
  'locales',
  'familyStatus',
  'professionalSituation',
  'educationLevel',
  'income',
  'devices',
  'techSavviness',
];

export type BaseOffer = Partial<Offer> & ShortOfferDto;
interface Props<T extends BaseOffer> {
  offer: T;
  onClick?: (offer: T) => any;
  selected?: boolean;
  duration?: number;
  recurrence?: Recurrence;
  category?: string;
  selectMode?: boolean;
  offerProductOrdersInfos?: OfferProductOrdersInfos;
  disabled?: boolean;
  hideCriteriaDetails?: boolean;
  countryPriceTiers?: string[];
  countryKeys?: string[];
}

export const OfferBlock = function <T extends BaseOffer>({
  offer,
  duration,
  recurrence,
  selected,
  onClick: onClickProps,
  category,
  selectMode,
  offerProductOrdersInfos,
  disabled,
  hideCriteriaDetails,
  countryPriceTiers = [],
  countryKeys,
}: Props<T>) {
  const { t } = useTranslation();

  const { mutate } = useSendAction('click_on_offer');
  const track = useTrackEvent('Clicked On Offer', offer);

  const allCriteriaAvailable = useMemo(() => {
    if (!offer.criteria || !offer.criteria.length) {
      return true;
    }
    if (offer.criteria.length !== allCriteria.length) {
      return false;
    }
    return !offer.criteria.some((c) => !allCriteria.includes(c));
  }, [offer.criteria]);

  const offerProducts = useMemo(() => {
    const products = getRelevantProducts(offer.products, {
      countryPriceGroupIds: countryPriceTiers,
    });

    return products.filter((op) => {
      if (op.product?.type !== ProductType.SUBSCRIPTION) {
        return true;
      }

      if (recurrence === Recurrence.YEARLY) {
        return op.product?.priceUnit !== ProductPriceUnit.EUR_MONTHLY;
      }
      return op.product?.priceUnit !== ProductPriceUnit.EUR_YEARLY;
    });
  }, [offer, countryPriceTiers, recurrence]);

  const countryGroups = useMemo(() => {
    const groups: string[] = [];
    for (const op of offerProducts) {
      if (op.countryPriceGroupId && !groups.includes(op.countryPriceGroupId)) {
        groups.push(op.countryPriceGroupId);
      }
    }
    if (!groups.length) {
      groups.push(NO_PRICE_GROUP);
    }
    return groups;
  }, [offerProducts]);

  const displayableProducts = useMemo(
    () =>
      (
        offerProducts
          .map(({ product }) => product)
          .filter((p) => p && isProductDisplayable(p, category)) as Product[]
      ).sort((a, b) => compareProducts(a, b, offerProductOrdersInfos)),
    [offerProducts, category, offerProductOrdersInfos],
  );

  const credits = offerProducts.reduce(
    (acc, { product }) => acc + (product?.credits || 0),
    0,
  );

  const priceObj = useMemo((): DisplayablePrice | undefined => {
    if (countryGroups.length !== 1) {
      return undefined;
    }
    const incentive = computeIncentive(offerProducts, duration);
    const price = sumProducts(offerProducts, 1, duration, incentive);
    const firstProduct = offerProducts.find(
      (op) => op.product?.price !== 0,
    )?.product;
    if (!firstProduct) {
      if (offer?.requireCallToSales) {
        if (offer.type === OfferType.CREDIT) {
          return {
            label: 'YOUR_AMOUNT',
            extraCredits: credits,
          };
        }
        return {
          label: 'CUSTOM',
        };
      }

      return {
        unit: DisplayablePriceUnit.EUR,
        price: price,
      };
    }

    if (firstProduct.pricePerParticipant) {
      return { price, unit: DisplayablePriceUnit.PER_PARTICIPANT };
    }

    if (
      firstProduct.priceUnit === ProductPriceUnit.EUR_MONTHLY ||
      firstProduct.priceUnit === ProductPriceUnit.EUR_YEARLY
    ) {
      const unit = recurrence
        ? (recurrence as string as DisplayablePriceUnit)
        : firstProduct.priceUnit === ProductPriceUnit.EUR_MONTHLY
        ? DisplayablePriceUnit.MONTHLY
        : DisplayablePriceUnit.YEARLY;

      return { unit, price };
    }

    return {
      unit: DisplayablePriceUnit.EUR,
      price,
      extraCredits: credits ? credits - price : undefined,
    };
  }, [countryGroups, offerProducts, offer, credits, duration, recurrence]);

  const onClick = useCallback(() => {
    if (selected) {
      return;
    }
    track();
    if (!selectMode) {
      mutate({
        offerId: offer.id,
        offer: {
          ...offer,
          products: undefined,
        },
      });
    }
    if (offer.requireCallToSales && offer.bookForCallLink) {
      window.open(offer.bookForCallLink, '_blank');
      return;
    }
    if (onClickProps && offer) {
      onClickProps(offer);
    }
  }, [onClickProps, offer, mutate, selected, track]);

  const buttonLabel = offer.requireCallToSales
    ? t(`billing.offers.block.cta.callToSales`)
    : t(
        `billing.offers.block.cta.${
          selected ? 'selectedOffer' : 'notSelectedOffer'
        }`,
        {
          context: offer.type,
        },
      );

  return (
    <OfferBlockDisplay
      key={offer.id}
      offerName={offer?.name || ''}
      description={offer.description}
      priceBlock={
        priceObj && (
          <PriceBlock
            priceObj={priceObj}
            showNoTax={!selectMode && offer.type === OfferType.SUBSCRIPTION}
            advantageAmount={offer.advantageAmount}
            first
          />
        )
      }
      color={(offer.color as any) || 'blue'}
      disabled={offer.disabled || disabled}
      tooltip={offer.tooltip}
      selected={selected}
      onClick={onClick}
      selectMode={selectMode}
      mostPopular={offer.mostPopular}
      buttonLabel={buttonLabel}
    >
      {offer.type === OfferType.CREDIT && (
        <OfferCreditItems offer={offer} credits={credits} />
      )}
      {offer.type === OfferType.TARGETING && countryGroups.length === 1 && (
        <OfferItem text={t('billing.offers.block.items.priceDetail')}>
          <PriceChips
            products={offerProducts}
            duration={duration || 60}
            offerName={offer.name}
            ink
          />
        </OfferItem>
      )}

      {offer.type === OfferType.TARGETING &&
        countryGroups.length > 1 &&
        countryKeys && (
          <OfferItem text={t('billing.offers.block.items.priceDetail')}>
            <PricesByCountries
              products={offerProducts}
              duration={duration}
              offerName={offer.name}
              countryKeys={countryKeys}
            />
          </OfferItem>
        )}

      {offer.type === OfferType.TARGETING && (
        <OfferItem fill text={t('billing.offers.block.items.criteria')}>
          {!hideCriteriaDetails && (
            <OfferCriteriaSubList
              criteria={allCriteriaAvailable ? allCriteria : offer.criteria}
            />
          )}
        </OfferItem>
      )}
      {displayableProducts.length > 1 &&
        displayableProducts.map((product) => (
          <ProductOfferItem key={product.id} product={product} />
        ))}

      {offer.type === OfferType.TARGETING && !!offer.products && (
        <OfferItem text={t('billing.offers.block.items.screeningQuestions')}>
          <OfferScreenerIncluded offer={offer} />
        </OfferItem>
      )}
    </OfferBlockDisplay>
  );
};
